import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useHistory } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
} from "@coreui/react";
import { Spinner } from "react-bootstrap";
import {
  ReceiptFormProvider,
  useReceiptFormContext,
} from "./context/ReceiptFormContext";
import ReceiptFormHeader from "./sections/ReceiptFormHeader";
import ReceiptFormAssociatedDocumentSection from "./sections/ReceiptFormAssociatedDocumentSection";
import ReceiptFormPaymentSection from "./sections/ReceiptFormPaymentSection";
import ReceiptFormDetailSection from "./sections/ReceiptFormDetailSection";
import Loading from "../indicators/Loading";
import NotFound from "../indicators/NotFound";
import ServerError from "../indicators/ServerError";
import ClientUpdateModal from "../client/ClientUpdateModal";
import Client from "../../models/client";
import { errorAlert, successAlert } from "../utils/messages";
import { getRoutePath } from "../../routes";
import {
  ItemRequestStatus,
  createItem,
  getItem,
  updateItem,
} from "../../api/generics";
import { NOT_FOUND, SUCCESS } from "../../utils/constants/tags";
import { changeTZToPyg, changeTZToUtc } from "../../utils/dates";
import { getStampById } from "../../api/stamp";
import { getEstablishmentById } from "../../api/establishment";
import { getDispatchPointById } from "../../api/dispatch-point";
import { getClientById } from "../../api/client";
import { getInvoiceById } from "../../api/invoice";
import { Receipt, RECEIPT_PAYMENT_METHOD_CASH } from "../../models/receipt";
import { Invoice } from "../../models/invoice";
import { PYG } from "../../currency/available-currencies";
import { ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE } from "../../models/associated-document";
import { useIdempotencyKey } from "../../hooks/use-idempotency-key";

const ReceiptFormInnerContent: React.FC = () => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );
  const stamps = useSelector(
    (state: RootState) => state.taxPayerParameters.data.stamps,
  );
  const establishments = useSelector(
    (state: RootState) => state.taxPayerParameters.data.establishments,
  );
  const dispatchPoints = useSelector(
    (state: RootState) => state.taxPayerParameters.data.dispatchPoints,
  );

  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadNotFound, setLoadNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittingDraft, setSubmittingDraft] = useState(false);
  const submissionLockRef = useRef(false);
  const idempotencyKey = useIdempotencyKey();
  const [showAddClient, setShowAddClient] = useState(false);

  const {
    receiptRef,
    setErrors,
    setReceiptDate,
    setStamp,
    setEstablishment,
    setDispatchPoint,
    setClient,
    setAssociatedDocumentType,
    setElectronicInvoice,
    setTotalAmount,
    setItems,
    setCurrency,
    setPaymentMethod,
    setPaymentMethods,
    onClientChange,
  } = useReceiptFormContext();

  const name = "Agregar Recibo";

  const getEditingReceiptId = () => {
    const urlParams = new URLSearchParams(history.location.search);
    const editingReceiptId = urlParams.get("id");

    if (editingReceiptId !== null) {
      if (!isNaN(parseInt(editingReceiptId))) {
        return parseInt(editingReceiptId);
      }
    }

    return undefined;
  };

  const loadInitialReceiptById = async (): Promise<Receipt | null> => {
    const editingId = getEditingReceiptId();

    if (editingId === undefined) {
      return null;
    }

    let requestStatus = await getItem<Receipt>(`/receipts/${editingId}/`);

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.status === NOT_FOUND) {
        setLoadNotFound(true);
      } else {
        setLoadError(true);
      }

      return null;
    }

    if (requestStatus.data === undefined || requestStatus.data === null) {
      setLoadError(true);
      return null;
    }

    return requestStatus.data!;
  };

  const loadInitialData = async () => {
    setLoading(true);
    const receipt = await loadInitialReceiptById();

    if (receipt === null) {
      setLoading(false);
      return;
    }

    const stampPromise = getStampById(receipt.stampId, receipt.taxPayerId);
    const establishmentPromise = getEstablishmentById(
      receipt.establishmentId,
      receipt.taxPayerId,
    );
    const dispatchPointPromise = getDispatchPointById(
      receipt.dispatchPointId,
      receipt.taxPayerId,
    );
    const clientPromise = getClientById(receipt.clientId, receipt.taxPayerId);

    let invoicePromise: Promise<Invoice | null> | null = null;

    if (receipt.invoiceId !== undefined) {
      invoicePromise = getInvoiceById(receipt.invoiceId);
    }

    const stamp = await stampPromise;
    const establishment = await establishmentPromise;
    const dispatchPoint = await dispatchPointPromise;
    const client = await clientPromise;

    receiptRef.current = receipt;

    const pygDate = new Date(receipt.receiptDate!);
    const year = pygDate.getUTCFullYear();
    const month = pygDate.getUTCMonth();
    const day = pygDate.getUTCDate();
    const hours = pygDate.getUTCHours();
    const minutes = pygDate.getUTCMinutes();
    const seconds = pygDate.getUTCSeconds();
    const receiptDate = new Date(year, month, day, hours, minutes, seconds);
    setReceiptDate(receiptDate);

    setStamp(stamp);
    setEstablishment(establishment);
    setDispatchPoint(dispatchPoint);
    setClient(client);

    if (invoicePromise !== null) {
      const previousInvoice = await invoicePromise;
      setElectronicInvoice(previousInvoice);
    }

    setTotalAmount(receipt.amount!);
    setCurrency(receipt.currency ?? PYG);
    setPaymentMethod(receipt.paymentMethod ?? RECEIPT_PAYMENT_METHOD_CASH);
    setPaymentMethods(receipt.paymentMethods ?? []);

    setAssociatedDocumentType(receipt.associatedDocumentType);

    // Load items if they exist
    if (receipt.items && receipt.items.length > 0) {
      setItems(receipt.items);
    }

    setLoading(false);
  };

  useEffect(() => {
    const newReceiptDate = changeTZToPyg(new Date());
    if (receiptRef.current !== null) {
      receiptRef.current.receiptDate =
        changeTZToUtc(newReceiptDate).toISOString();
      setReceiptDate(newReceiptDate);
    }
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select stamp when only one exists
  useEffect(() => {
    if (
      stamps.length === 1 &&
      receiptRef.current?.id === undefined &&
      receiptRef.current !== null
    ) {
      const stamp = stamps[0];
      receiptRef.current.stampId = stamp.id;
      receiptRef.current.stampIdentifier = stamp.identifier;
      receiptRef.current.stampBeginDate = stamp.beginDate;
      setStamp(stamp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamps]);

  // Auto-select establishment when only one exists
  useEffect(() => {
    if (
      establishments.length === 1 &&
      receiptRef.current?.id === undefined &&
      receiptRef.current !== null
    ) {
      const establishment = establishments[0];
      receiptRef.current.establishmentId = establishment.id;
      receiptRef.current.establishmentCode = establishment.code;
      setEstablishment(establishment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [establishments]);

  // Auto-select dispatch point when only one exists
  useEffect(() => {
    if (
      dispatchPoints.length === 1 &&
      receiptRef.current?.id === undefined &&
      receiptRef.current !== null
    ) {
      const dispatchPoint = dispatchPoints[0];
      receiptRef.current.dispatchPointId = dispatchPoint.id;
      receiptRef.current.dispatchPointIdentifier = dispatchPoint.identifier;
      setDispatchPoint(dispatchPoint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchPoints]);

  const onAddClientClick = () => {
    setShowAddClient(true);
  };

  const onAddClientSuccess = (newClient?: Client) => {
    let message = "Cliente agregado correctamente";
    onClientChange(newClient ? newClient : null);
    if (newClient) {
      message = `Cliente ${newClient.socialName} agregado correctamente`;
    }
    successAlert(message);
    setShowAddClient(false);
  };

  const onAddClientCancel = () => {
    setShowAddClient(false);
  };

  const onSave = async () => {
    if (receiptRef.current === null || submissionLockRef.current) {
      return;
    }

    submissionLockRef.current = true;
    setSubmitting(true);

    let requestPromise: Promise<ItemRequestStatus<Receipt>>;
    let successMessage = "Recibo agregada exitósamente";

    const receipt = receiptRef.current;
    receipt.taxPayerRuc = taxPayer.ruc;
    receipt.taxPayerSocialName = taxPayer.socialName;
    receipt.taxPayerFantasyName = taxPayer.fantasyName;

    if (receipt.associatedDocumentType === undefined) {
      receipt.associatedDocumentType =
        ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE;
    }

    if (receipt.currency === undefined) {
      receipt.currency = PYG;
    }

    if (receipt.paymentMethod === undefined) {
      receipt.paymentMethod = RECEIPT_PAYMENT_METHOD_CASH;
    }

    if (receipt.id === undefined) {
      requestPromise = createItem<Receipt>("/receipts/create/", receipt, {
        "Idempotency-Key": idempotencyKey,
      });
    } else {
      requestPromise = updateItem<Receipt>(`/receipts/${receipt.id}/`, receipt);
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";

      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
      setSubmitting(false);
      submissionLockRef.current = false;
    } else {
      successAlert(successMessage);
      history.push(getRoutePath("receipts"));
    }
  };

  const onSaveDraft = async () => {
    if (receiptRef.current === null || submissionLockRef.current) {
      return;
    }

    submissionLockRef.current = true;
    setSubmittingDraft(true);

    const receipt = receiptRef.current;
    receipt.taxPayerRuc = taxPayer.ruc;
    receipt.taxPayerSocialName = taxPayer.socialName;
    receipt.taxPayerFantasyName = taxPayer.fantasyName;

    if (receipt.associatedDocumentType === undefined) {
      receipt.associatedDocumentType =
        ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE;
    }

    if (receipt.currency === undefined) {
      receipt.currency = PYG;
    }

    if (receipt.paymentMethod === undefined) {
      receipt.paymentMethod = RECEIPT_PAYMENT_METHOD_CASH;
    }

    const requestStatus = await createItem<Receipt>("/receipts/create/", {
      ...receipt,
      isDraft: true,
    }, {
      "Idempotency-Key": idempotencyKey,
    });

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }
      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
      setSubmittingDraft(false);
      submissionLockRef.current = false;
    } else {
      successAlert("Borrador guardado exitósamente");
      history.push(getRoutePath("receipts"));
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (loadNotFound) {
    return <NotFound />;
  }

  if (loadError) {
    return <ServerError />;
  }

  return (
    <>
      <CCard className="shadow-sm border-0">
        <CCardHeader
          style={{
            background: "linear-gradient(180deg, #006657 0%, #095e55 100%)",
            color: "white",
            borderBottom: "none",
            fontWeight: 600,
            fontSize: "1rem",
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <i className="fa fa-file-text-o" style={{ fontSize: "1rem" }}></i>
          {name}
        </CCardHeader>
        <CCardBody>
          <ReceiptFormHeader onAddClientClick={onAddClientClick} />
          <ReceiptFormAssociatedDocumentSection />
          <ReceiptFormPaymentSection />
          <ReceiptFormDetailSection />
        </CCardBody>
        <CCardFooter
          style={{
            background: "var(--theme-surface-raised)",
            borderTop: "1px solid var(--theme-divider)",
            padding: "14px 20px",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            <CButton
              type="submit"
              color="primary"
              onClick={onSave}
              disabled={submitting || submittingDraft}
              style={{ minWidth: "130px", fontWeight: 600, padding: "8px 20px" }}
            >
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <i className="fa fa-save mr-2"></i>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
            {receiptRef.current?.id === undefined && (
              <CButton
                type="button"
                color="secondary"
                onClick={onSaveDraft}
                disabled={submitting || submittingDraft}
                style={{ minWidth: "180px", fontWeight: 600, padding: "8px 20px" }}
              >
                {submittingDraft ? (
                  <Spinner
                    animation="grow"
                    style={{
                      height: "17px",
                      width: "17px",
                      marginTop: "auto",
                      marginBottom: "auto",
                      marginRight: "10px",
                    }}
                  />
                ) : (
                  <i className="fa fa-edit mr-2"></i>
                )}
                {submittingDraft ? "Guardando..." : "Guardar como borrador"}
              </CButton>
            )}
          </div>
        </CCardFooter>
      </CCard>
      <ClientUpdateModal
        show={showAddClient}
        onSuccess={onAddClientSuccess}
        onCancel={onAddClientCancel}
      />
    </>
  );
};

const ReceiptFormWithContext: React.FC = () => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  return (
    <ReceiptFormProvider taxPayerId={taxPayer.id}>
      <ReceiptFormInnerContent />
    </ReceiptFormProvider>
  );
};

export default ReceiptFormWithContext;
